import logger from "../../../utils/logger";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

// react table
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchPrimPayments,
  fetchPrimPaymentsByCompany,
} from "../../../slices/apps/orderSlice";
import { fetchPrimSummary } from "../../../slices/apps/companySlice";

// Bileşenler
import DebouncedInput from "../../../Components/Common/DebouncedInput";
import TablePagination from "../../../Components/Common/TablePagination";
import PrimPaymentConfirmModal from "./PrimPaymentConfirmModal";
import PrimPaymentCustomersModal from "./PrimPaymentCustomersModal";

// moment
import moment from "moment";
import "moment/locale/tr";
moment.locale("tr");

// Sütun Tanımları
const columnHelper = createColumnHelper();

const pageSizeOptions = [10, 25, 50, 100, 200];

const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
});

const PrimPaymentTable = () => {
  const dispatch = useDispatch();

  const { primPayments, primPaymentsLoading, isUserData } = useSelector(
    (state) => {
      return {
        primPayments: state.order.primPayments,
        primPaymentsLoading: state.order.primPaymentsLoading,
        isUserData: state.auth.isUserData,
      };
    },
    shallowEqual
  );

  // States
  const [data, setData] = useState([]);
  const [isTableMode, setIsTableMode] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchData = async () => {
    try {
      if (isTableMode) {
        await dispatch(fetchPrimPaymentsByCompany()).unwrap();
      } else {
        await dispatch(fetchPrimPayments()).unwrap();
      }
    } catch (error) {
      logger.error("Veri çekme hatası:", error);
    }
  };

  const handleRefresh = async () => {
    await fetchData();
    await dispatch(fetchPrimSummary()).unwrap();
  };

  useEffect(() => {
    fetchData();
  }, [isTableMode]);

  useEffect(() => {
    if (!primPayments) {
      setData([]);
      return;
    }

    const filteredData = primPayments?.filter((data) => {
      if (globalFilter) {
        const searchTerm = globalFilter.toLocaleLowerCase("tr");
        const searchableFields = [
          data.primPayReqId,
          data.earnedCompanyName,
          data.totalPrimAmount,
        ];
        const match = searchableFields.some(
          (field) =>
            field &&
            field.toString().toLocaleLowerCase("tr").includes(searchTerm)
        );
        if (!match) return false;
      }
      return true;
    });

    setData(filteredData || []);
  }, [globalFilter, primPayments]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "action",
        header: () => null,
        size: 46,
        minSize: 46,
        maxSize: 46,
        enableResizing: false,
        cell: ({ row }) => (
          <PrimPaymentCustomersModal
            id={
              isTableMode
                ? row.original.earnedCompanyId
                : row.original.primPayReqId
            }
            isMode={isTableMode}
          />
        ),
      }),

      ...(!isTableMode
        ? [
            columnHelper.display({
              id: "actions",
              header: () => null,
              size: 46,
              minSize: 46,
              maxSize: 46,
              enableResizing: false,
              cell: ({ row }) => (
                <div className="d-flex gap-3">
                  {!row.original.isPaidPrim && isUserData?.role == "Admin" ? (
                    <PrimPaymentConfirmModal id={row.original.primPayReqId} />
                  ) : (
                    <span
                      style={{
                        display: "flex",
                        padding: "3px",
                      }}
                    >
                      <i className="ri-money-dollar-circle-line fs-18 text-muted" />
                    </span>
                  )}
                </div>
              ),
            }),
          ]
        : []),

      columnHelper.accessor("earnedCompanyName", {
        header: "Firma Adı",
        size: "auto",
        minSize: 150,
        maxSize: 250,
        cell: ({ row }) => (
          <div className="d-flex flex-column">
            <div className="text-truncate fw-bold fs-12">
              {row.original.earnedCompanyName}
            </div>
            <div className="text-truncate fw-medium fs-12">
              {row.original.description || "-"}
            </div>
          </div>
        ),
      }),

      columnHelper.accessor("isPaidPrim", {
        header: "Durum",
        size: 125,
        minSize: 125,
        maxSize: 125,
        cell: ({ row }) => (
          <div>
            {isTableMode ? (
              <div className="badge bg-warning-subtle text-warning">
                Talep Bekliyor
              </div>
            ) : row.original.isPaidPrim ? (
              <div className="badge bg-success-subtle text-success">Ödendi</div>
            ) : (
              <div className="badge bg-warning-subtle text-warning">
                Ödeme Bekliyor
              </div>
            )}
          </div>
        ),
      }),

      columnHelper.accessor("primPayReqDate", {
        header: "Talep Tarihi",
        size: 125,
        minSize: 125,
        maxSize: 125,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.primPayReqDate
              ? moment(row.original.primPayReqDate).format("DD.MM.YYYY")
              : "-"}
          </div>
        ),
      }),

      columnHelper.accessor("invoiceNo", {
        header: "Fatura No",
        size: 125,
        minSize: 125,
        maxSize: 125,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.invoiceNo ? row.original.invoiceNo : "-"}
          </div>
        ),
      }),

      columnHelper.accessor("invoiceDate", {
        header: "Fatura Tarihi",
        size: 125,
        minSize: 125,
        maxSize: 125,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.invoiceDate
              ? moment(row.original.invoiceDate).format("DD.MM.YYYY")
              : "-"}
          </div>
        ),
      }),

      columnHelper.accessor("totalPrimCount", {
        header: "Toplam Prim",
        size: 160,
        minSize: 160,
        maxSize: 160,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.totalPrimCount} Adet
          </div>
        ),
      }),

      columnHelper.accessor("totalPrimAmount", {
        header: "Toplam Tutar",
        size: 160,
        minSize: 160,
        maxSize: 160,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {currencyFormatter.format(row.original.totalPrimAmount)}
          </div>
        ),
      }),

      columnHelper.accessor("isPaidDate", {
        header: "Ödeme Tarihi",
        size: 125,
        minSize: 125,
        maxSize: 125,

        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.isPaidDate
              ? moment(row.original.isPaidDate).format("DD.MM.YYYY")
              : "-"}
          </div>
        ),
      }),
    ],
    [isTableMode]
  );

  const table = useReactTable({
    data: data,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  const toggleDataMode = () => {
    setIsTableMode((prevMode) => !prevMode);
  };

  return (
    <Fragment>
      <Card>
        <CardBody className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-label btn"
            color="warning"
            type="button"
            onClick={() => handleRefresh()}
          >
            <i className="ri-refresh-line label-icon align-middle me-2"></i>
            Yenile
          </Button>

          <Button
            className={`btn btn-label btn ${
              isTableMode ? "btn-primary" : "btn-primary"
            }`}
            color={isTableMode ? "primary" : "primary"}
            type="button"
            onClick={() => toggleDataMode()}
          >
            <i className="ri-exchange-line label-icon align-middle me-2"></i>
            {isTableMode
              ? "Talep Edilenleri Göster"
              : "Talep Edilmeyenleri Göster"}
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1 fs-14">
            Kayıt Primi Listesi
          </h4>
          <div className="flex-shrink-0"></div>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
              <form>
                <div className="d-flex flex-wrap justify-content-between">
                  <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                    <div style={{ width: "100px" }}>
                      <select
                        className="form-select mb-3"
                        aria-label="Sayfa başına kayıt sayısı"
                        value={getState().pagination.pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        {pageSizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div
                      className="search-box me-2 mb-2 d-inline-block border"
                      style={{ borderRadius: "0.25em" }}
                    >
                      <DebouncedInput
                        value={globalFilter ?? ""}
                        onChange={(value) => setGlobalFilter(String(value))}
                        placeholder={"Ara..."}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </form>
            </CardBody>
          </Row>

          <div style={{ overflowX: "auto" }}>
            <Table className="align-middle">
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-muted"
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize,
                          letterSpacing: "0.8px",
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: header.column.getCanSort()
                                ? "pointer"
                                : "default",
                              userSelect: "none",
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <i className="ri-arrow-down-s-line"></i>,
                              desc: <i className="ri-arrow-up-s-line"></i>,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {primPaymentsLoading ? (
                  <tr>
                    <td colSpan="100%" className="text-center pt-5 pb-5">
                      <Spinner color="primary" />
                      <p className="text-dark">Yükleniyor...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="100%"
                      className="text-center pt-5 pb-5 text-muted"
                    >
                      <h1>
                        <i className="ri-error-warning-line text-muted"></i>
                      </h1>
                      <p>Kayıt bulunamadı!</p>
                    </td>
                  </tr>
                ) : (
                  getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.columnDef.size,
                            minWidth: cell.column.columnDef.minSize,
                            maxWidth: cell.column.columnDef.maxSize,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <TablePagination table={table} />
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default PrimPaymentTable;
